import { Version } from "src/app/shared/model/gestion-proyectos/etapa/version.model";

/*
 * Etapas y versiones de Gestión de Proyectos: los contratos de los endpoints
 * del módulo (plan docs/superpowers/plans/2026-10-13-etapas-y-versiones.md
 * §6.1). Son el espejo de los beans de CosCore
 * mx.com.solser.cos.beans.proyecto.version; si uno cambia, cambia el otro.
 *
 * El servidor calcula todo (estado de etapa, vigente y cerrada, conteos,
 * nombre sugerido, orígenes QA y lo que puede el usuario); la pantalla lo
 * pinta sin recalcular. Los rechazos viajan en pCodRetorno/pMensaje.
 */

/** Estado calculado de una etapa (EstadoEtapaEnum). */
export type CodigoEstadoEtapa = "POR_INICIAR" | "ACTIVA" | "TERMINADA";

/** Estado calculado de una versión (EstadoVersionEnum): la de mayor orden de su etapa es la vigente. */
export type EstadoVersion = "VIGENTE" | "CERRADA";

/** Ambiente de una versión (AmbienteVersionEnum). Una PRODUCTIVO sale de una QA de la misma etapa (D3). */
export type AmbienteVersion = "QA" | "PRODUCTIVO";

export const AMBIENTES_VERSION: readonly AmbienteVersion[] = ["QA", "PRODUCTIVO"];

/** Topes de VersionesProyectoBuss: el servidor rechaza lo que pase de ahí (no recorta). */
export const LARGO_NOMBRE_VERSION = 50;
export const LARGO_RUTA_VERSION = 600;
export const LARGO_DETALLE_VERSION = 500;

/** Códigos de respuesta del módulo y del guardado completo del proyecto (§5.8). */
export const CODIGOS_VERSIONES = {
  EXITO: "00000",
  /** Otra versión entró mientras se capturaba; la respuesta trae la etapa al día. */
  CONFLICTO: "VAL-VER-CONFLICTO",
  NOMBRE: "VAL-VER-NOMBRE",
  ORIGEN: "VAL-VER-ORIGEN",
  /** Proyecto CERRADO o CIERRE (D8). */
  CERRADO: "VAL-VER-CERRADO",
  /** guardarProyecto recibió una versión sin id: el modal viejo en caché. */
  FRONT_VIEJO: "VAL-VER-FRONT-VIEJO",
  /** guardarProyecto quitaba una etapa con versiones o incidencias (D11). */
  ETAPA_CON_HISTORIAL: "VAL-ETAPA-CON-HISTORIAL",
  /** guardarProyecto quitaba una etapa y no pudo contar sus incidencias: no guardó nada. */
  ETAPA_SIN_VERIFICAR: "VAL-ETAPA-SIN-VERIFICAR",
} as const;

/** Lo que traen todas las respuestas del módulo (BaseBean). */
export interface RespuestaVersiones {
  pCodRetorno: string;
  pMensaje: string | null;
}

/** EstadoEtapaBean: el estado y su texto, como los calculó el servidor. */
export interface EstadoEtapa {
  codigo: CodigoEstadoEtapa | null;
  /** ACTIVA porque ya pasó la fecha fin y quedan incidencias abiertas. */
  fueraDeFecha: boolean;
  diaActual: number | null;
  diasTotales: number | null;
  /** «Día 13 de 31», «Fuera de fecha», «Inicia el 26/10/2026», «Terminó el 31/08/2026». */
  texto: string | null;
}

/** ReferenciaVersionBean: la vigente, un origen QA o la versión donde se detectó una incidencia. */
export interface ReferenciaVersion {
  id: string | null;
  nombre: string | null;
  ambiente: string | null;
}

/** IndicadoresEtapaBean. «abiertas» es D1: todo menos VOBO y CANCELADO. */
export interface IndicadoresEtapa {
  versiones: number;
  incidencias: number;
  versionesConDetectadas: number;
  abiertas: number;
  porValidar: number;
  conVobo: number;
  pendientesVersion: number;
  sinVersionIdentificada: number;
  /** Ejecuciones de pruebas del tenant en las versiones de la etapa (Fase 4). */
  ejecuciones: number;
  /** La última ejecución de la etapa; null sin ejecuciones. */
  ultimaPrueba: UltimaPrueba | null;
}

/** VersionResumenBean: una versión en A1 y en la columna izquierda de T1. */
export interface VersionResumen {
  id: string | null;
  /** Orden en la etapa (el guardado, o la posición + 1 en las versiones anteriores). */
  consecutivo: number | null;
  nombre: string | null;
  detalle: string | null;
  ruta: string | null;
  ambiente: string | null;
  idVersionOrigen: string | null;
  /** Instante ISO-8601 que puso el servidor; null en las versiones anteriores a la Fase 1A. */
  fechaCreacion: string | null;
  /** Sin fechaCreacion: la fecha (yyyy-MM-dd) que trae el nombre V<n>-dd-MM-yyyy (D15). */
  fechaAprox: string | null;
  nombreUsuarioCrea: string | null;
  estado: EstadoVersion | null;
  cerradaDesde: string | null;
  cerradaDesdeAprox: boolean;
  detectadas: number;
  detectadasAbiertas: number;
  resueltasAqui: number;
  /** Ejecuciones de pruebas del tenant en esta versión (Fase 4). */
  ejecuciones: number;
  /** La última ejecución de la versión; null sin ejecuciones. */
  ultimaPrueba: UltimaPrueba | null;
}

/** EtapaVersionesBean: una etapa con su estado, indicadores, versiones y lo que puede el usuario. */
export interface EtapaVersiones {
  /** El _id de la etapa en Mongo; null en una etapa guardada sin identificador. */
  idEtapa: string | null;
  nombre: string | null;
  /** Como está guardada (texto en formatos mezclados). */
  fechaInicio: string | null;
  fechaFin: string | null;
  idLiderEtapa: number | null;
  nombreLider: string | null;
  idLiderEtapaQA: number | null;
  nombreLiderQA: string | null;
  estado: EstadoEtapa | null;
  /** D4 y D8: la pantalla oculta o bloquea la acción; el servidor valida igual al escribir. */
  puedeCrearVersion: boolean;
  puedeRegistrarPruebas: boolean;
  /**
   * Bandera de T3b del corporativo (CONF_VERSIONES_PENDIENTES): con ella, T1 y
   * A1 dicen que las resoluciones se entregan en la siguiente versión QA.
   */
  entregaAutomatica: boolean;
  indicadores: IndicadoresEtapa | null;
  vigente: ReferenciaVersion | null;
  siguienteConsecutivo: number;
  /** V{n}-dd-MM-yyyy, único en la etapa sin distinguir mayúsculas (D14). */
  nombreSugerido: string | null;
  /** Versiones QA que todavía no son origen de una PRODUCTIVO, de la más nueva a la más vieja. */
  origenesQA: ReferenciaVersion[];
  /** En su orden (consecutivo). */
  versiones: VersionResumen[];
  /** El ciclo que tomaría la siguiente ejecución de cada tipo (D16): «Se registrará como «Regresión 2»». */
  siguienteCiclo: CiclosPorTipo;
  /** Quienes ejecutan pruebas en la etapa (el líder QA, el líder y los recursos): el selector de T2. */
  ejecutores: Ejecutor[];
  /**
   * Las resoluciones que esperan versión (T3b), por folio: las entrega la
   * siguiente versión QA que se cree en la etapa (D3). Existen aunque la
   * bandera ya se haya apagado.
   */
  pendientes: IncidenciaVersion[];
}

/** EtapasVersionesResponse (PROYECTOS_ETAPAS_VERSIONES). */
export interface EtapasVersionesRespuesta extends RespuestaVersiones {
  idProyecto: string | null;
  etapas: EtapaVersiones[];
}

/** IncidenciaVersionBean: una resolución pendiente o entregada (Fase 3), detectada o resuelta (Fase 4). */
export interface IncidenciaVersion {
  idIncidencia: number;
  /** El _id de su etapa (cos_incidencia_qa.id_etapa). */
  idEtapa: string | null;
  folio: number | null;
  titulo: string | null;
  prioridad: string | null;
  estatus: string | null;
  detectadaEn: ReferenciaVersion | null;
  idUsuarioResponsable: number | null;
  responsable: string | null;
  idUsuarioResolvio: number | null;
  resolvio: string | null;
  fechaResolucion: string | null;
}

/** CrearVersionRequest (PROYECTOS_VERSION_CREAR). */
export interface CrearVersionPeticion {
  /** UUID que genera el diálogo al abrir: la llave de idempotencia. */
  id: string;
  idProyecto: string;
  idEtapa: string;
  nombre: string;
  ambiente: AmbienteVersion;
  /** Solo PRODUCTIVO. */
  idVersionOrigen: string | null;
  ruta: string | null;
  detalle: string | null;
  /** La vigente que vio el usuario; null = la etapa no tenía versiones. */
  idVersionVigenteEsperada: string | null;
}

/** CrearVersionResponse: con VAL-VER-CONFLICTO, «etapa» trae el contexto nuevo. */
export interface CrearVersionRespuesta extends RespuestaVersiones {
  version: Version | null;
  versionCerrada: Version | null;
  incidenciasEntregadas: IncidenciaVersion[];
  etapa: EtapaVersiones | null;
}

/* ============================ Página y resumen (Fase 2) ============================ */

/** Tope de proyectos por llamada a PROYECTOS_ETAPAS_RESUMEN; con más, el servidor responde 00002. */
export const MAXIMO_PROYECTOS_RESUMEN = 50;

/** UltimaPruebaBean: la ejecución más reciente de la etapa o de la versión; null sin ejecuciones. */
export interface UltimaPrueba {
  /** FUNCIONAL, REGRESION, HUMO o ACEPTACION_USUARIO. */
  tipo: string | null;
  /** Consecutivo por etapa y tipo (D16): «Regresión 2». */
  ciclo: number | null;
  /** yyyy-MM-dd */
  fecha: string | null;
  aprobados: number | null;
  ejecutados: number | null;
  /** APROBADA, CON_INCIDENCIAS o BLOQUEADA. */
  resultado: string | null;
}

/** ResumenEtapaBean: una etapa en la tabla compacta de la tarjeta (A0b). */
export interface ResumenEtapa {
  idEtapa: string | null;
  nombre: string | null;
  fechaInicio: string | null;
  fechaFin: string | null;
  estado: EstadoEtapa | null;
  /** La vigente (cualquier ambiente); null si la etapa no tiene versiones. */
  vigente: ReferenciaVersion | null;
  totalVersiones: number;
  ultimaPrueba: UltimaPrueba | null;
  /** D1, incluidas las de «sin versión identificada». */
  abiertas: number;
  /** RESUELTO cuya resolución espera versión (T3b); 0 mientras no haya centinela. */
  pendientesVersion: number;
  puedeCrearVersion: boolean;
  /** D4 con los recursos de la etapa y D8. */
  puedeRegistrarPruebas: boolean;
  /** Ejecuciones de pruebas del tenant en la etapa. */
  ejecuciones: number;
  siguienteCiclo: CiclosPorTipo;
}

/** ResumenProyectoEtapasBean: las etapas de un proyecto (por fecha de inicio, D12) y sus defectos con tenant. */
export interface ResumenProyectoEtapas {
  idProyecto: string | null;
  totalDefectos: number;
  /** Bandera de T3b del corporativo (CONF_VERSIONES_PENDIENTES); falsa sin renglón. */
  entregaAutomatica: boolean;
  etapas: ResumenEtapa[];
}

/** ResumenEtapasResponse (PROYECTOS_ETAPAS_RESUMEN): por _id de proyecto, en el orden pedido; los ajenos no vienen. */
export interface ResumenEtapasRespuesta extends RespuestaVersiones {
  proyectos: Record<string, ResumenProyectoEtapas>;
}

/* ============================ Pruebas y detalle de versión (Fase 4) ============================ */

/** TipoPruebaEnum. */
export type TipoPrueba = "FUNCIONAL" | "REGRESION" | "HUMO" | "ACEPTACION_USUARIO";

/** ResultadoPruebaEnum. */
export type ResultadoPrueba = "APROBADA" | "CON_INCIDENCIAS" | "BLOQUEADA";

/** El siguiente ciclo de cada tipo (llave = TipoPrueba): lo asigna el servidor al registrar (D16). */
export type CiclosPorTipo = Partial<Record<string, number>>;

/** Topes de EjecucionPruebaBuss y EvidenciaPruebaArchivos (D17); el servidor rechaza lo que pase de ahí. */
export const LARGO_OBSERVACIONES_PRUEBA = 2000;
export const MAXIMO_EVIDENCIAS_PRUEBA = 10;
/** EvidenciaPruebaArchivos.MAX_BYTES_PETICION: 50 MB entre los archivos de una petición (D17). */
export const MAXIMO_BYTES_PETICION_PRUEBA = 50 * 1024 * 1024;

/** Códigos de las pruebas (ReglasEjecucionPrueba, EjecucionPruebaBuss, EvidenciaPruebaArchivos). */
export const CODIGOS_PRUEBAS = {
  /** El ciclo chocó dos veces, o la edición perdió contra otra: no se guardó nada. */
  CONFLICTO: "VAL-PRU-CONFLICTO",
} as const;

/** EjecutorBean: quien puede ejecutar pruebas en la etapa, con su rol en ella. */
export interface Ejecutor {
  idEmpleado: number;
  nombre: string | null;
  /** «Líder QA», «Líder de la etapa» o el puesto del recurso. */
  rol: string | null;
}

/** EvidenciaPrueba: el archivo en disco (uuid.ext, lo pone el servidor) y el nombre con que se subió. */
export interface EvidenciaPrueba {
  archivo: string;
  nombreOriginal: string | null;
  tamano: number | null;
}

/** EjecucionPrueba (colección proyectoEjecucionPrueba, §4.2). */
export interface EjecucionPrueba {
  id: string;
  idProyecto: string | null;
  idEtapa: string | null;
  idVersion: string | null;
  tipo: TipoPrueba | null;
  /** Consecutivo por etapa y tipo (D16). */
  ciclo: number | null;
  /** yyyy-MM-dd */
  fechaEjecucion: string | null;
  idEmpleadoEjecuto: number | null;
  nombreEjecuto: string | null;
  resultado: ResultadoPrueba | null;
  casosEjecutados: number | null;
  casosAprobados: number | null;
  casosFallidos: number | null;
  /** Ids de cos_incidencia_qa (no el folio). */
  incidencias: number[];
  evidencias: EvidenciaPrueba[];
  observaciones: string | null;
  /** Instantes ISO-8601 que pone el servidor. */
  fechaRegistro: string | null;
  fechaActualizacion: string | null;
  /** Calculado al leer (A2): el rol de quien ejecutó en la etapa de hoy; null si ya no está en ella. */
  rolEjecuto?: string | null;
}

/** La parte «ejecucion» de PROYECTOS_PRUEBAS_GUARDAR: sin id registra; con id edita (sin cambiar versión, tipo ni ciclo, D5). */
export interface EjecucionPruebaPeticion {
  id: string | null;
  idProyecto: string;
  idEtapa: string;
  idVersion: string;
  tipo: TipoPrueba;
  fechaEjecucion: string;
  idEmpleadoEjecuto: number;
  resultado: ResultadoPrueba;
  casosEjecutados: number;
  casosAprobados: number;
  casosFallidos: number;
  incidencias: number[];
  observaciones: string | null;
  /** Edición: los archivos (uuid.ext) que se quitan de la ejecución; se quedan en disco (D5). */
  evidenciasQuitar: string[];
  /**
   * Edición: la fechaActualizacion con que llegó la ejecución (tal cual; null
   * si nunca se editó). Si ya no es la guardada, VAL-PRU-CONFLICTO.
   */
  fechaActualizacion: string | null;
}

/** EjecucionPruebaResponse. */
export interface EjecucionPruebaRespuesta extends RespuestaVersiones {
  ejecucion: EjecucionPrueba | null;
}

/** ReferenciaProyectoBean. */
export interface ReferenciaProyecto {
  id: string | null;
  nombre: string | null;
}

/** ReferenciaEtapaBean; las fechas (como están guardadas) solo llegan en A2. */
export interface ReferenciaEtapa {
  id: string | null;
  nombre: string | null;
  fechaInicio: string | null;
  fechaFin: string | null;
}

/** IndicadoresVersionBean: los casos son los de la última ejecución (D20); null sin ejecuciones. */
export interface IndicadoresVersion {
  casosEjecutados: number | null;
  casosAprobados: number | null;
  casosFallidos: number | null;
  ejecuciones: number;
  detectadas: number;
  detectadasAbiertas: number;
  resueltasAqui: number;
}

/** DetalleVersionBean (PROYECTOS_VERSION_DETALLE, A2). Las ejecuciones vienen de la más reciente a la más vieja. */
export interface DetalleVersion extends RespuestaVersiones {
  proyecto: ReferenciaProyecto | null;
  etapa: ReferenciaEtapa | null;
  version: VersionResumen | null;
  puedeEditarVersion: boolean;
  puedeRegistrarPruebas: boolean;
  indicadores: IndicadoresVersion | null;
  ejecuciones: EjecucionPrueba[];
  detectadas: IncidenciaVersion[];
  resueltas: IncidenciaVersion[];
  ejecutores: Ejecutor[];
  siguienteCiclo: CiclosPorTipo;
}

/**
 * EditarVersionRequest (PROYECTOS_VERSION_EDITAR): solo nombre, ruta y
 * descripción (§5.5). Los «esperados» son los de la versión como la mostró
 * el diálogo, tal cual: si ya no es esa, VAL-VER-CONFLICTO.
 */
export interface EditarVersionPeticion {
  idProyecto: string;
  idEtapa: string;
  id: string;
  nombre: string;
  ruta: string | null;
  detalle: string | null;
  nombreEsperado: string | null;
  rutaEsperada: string | null;
  detalleEsperado: string | null;
}

/** EditarVersionResponse; «Sin cambios» también es 00000. */
export interface EditarVersionRespuesta extends RespuestaVersiones {
  version: Version | null;
}

/**
 * Una incidencia de la etapa para vincularla a una ejecución (T2): lo que
 * trae CONSINCXFILTROQA (consultaIncidenciasXFiltroQA.do) o lo que responde
 * el alta (guardarIncidenciaQA.do).
 */
export interface IncidenciaDeEtapa {
  /** cos_incidencia_qa.id: lo que se guarda en la ejecución. */
  id: number;
  folio: number | null;
  titulo: string | null;
  prioridad: string | null;
  estatus: string | null;
  /** El id de la versión detectada (cos_incidencia_qa.version). */
  version: string | null;
}
